// Season editor "Sensors" section — READ-ONLY for now: the sensors table
// (Type | ID | Block), a detail panel for the selected sensor and a map with the
// sensor pins. Sensors come from each season's DETAIL (GET v2/seasons/...),
// never from the tree. Editing is not wired; the season PUT sends the detail's
// sensors unchanged, exactly as V1 does.
const React = require('react');
const { useState, useMemo } = require('react');
const { useTranslation } = require('react-i18next');
const dayjs = require('../../utils/dayjsConfig');
const BlocksMap = require('./BlocksMap');

const sensorRowsFor = (rows, details) =>
  rows.reduce((sensorRows, row) => {
    const sensors = details[row.season.id]?.season?.sensors || {};
    Object.keys(sensors).forEach((serial) => {
      sensorRows.push({
        key: row.season.id + ':' + serial,
        serial,
        sensor: sensors[serial],
        blockName: row.block.name,
      });
    });
    return sensorRows;
  }, []);

const DetailStatus = ({ rows, details }) => {
  const { t } = useTranslation();
  const failed = rows.find((row) => details[row.season.id]?.error);
  if (failed) {
    return (
      <div className='ns-error-text'>
        {t('new_season_detail_failed', {
          message: details[failed.season.id].error,
        })}
      </div>
    );
  }
  return rows.some((row) => !details[row.season.id]) ? (
    <div className='ns-muted'>{t('loading')}</div>
  ) : null;
};

const coordinatesOf = (sensor) => {
  const coordinates = sensor?.coordinates;
  if (!Array.isArray(coordinates) || coordinates.length < 2) {
    return null;
  }
  const lat = parseFloat(coordinates[0]);
  const lng = parseFloat(coordinates[1]);
  return isNaN(lat) || isNaN(lng) ? null : { lat, lng };
};

// fert_meters is keyed by fertilizer type: {<type>: {id, start_date, ...}}.
const firstFertMeter = (sensor) => {
  const meters = sensor?.fert_meters || {};
  const type = Object.keys(meters)[0];
  return type ? { type, ...(meters[type] || {}) } : null;
};

const formatUnixDate = (value) =>
  value ? dayjs.unix(value).format('DD MMM YYYY') : '';

const Detail = ({ label, value }) => (
  <div className='ns-form-row'>
    <label className='ns-form-label'>{label}</label>
    <input className='form-control ns-input' value={value ?? ''} readOnly />
  </div>
);

const SensorDetails = ({ sensorRow }) => {
  const { t } = useTranslation();
  if (!sensorRow) {
    return null;
  }
  const { sensor, serial } = sensorRow;
  const coordinates = coordinatesOf(sensor);
  const fertMeter = firstFertMeter(sensor);
  return (
    <div className='ns-sensor-details'>
      <Detail label={t('new_season_label')} value={sensor.type} />
      <Detail
        label={t('new_season_transmitter_id')}
        value={sensor.transmitter_id || serial}
      />
      <Detail label={t('name')} value={sensor.name} />
      <Detail label={t('latitude')} value={coordinates?.lat} />
      <Detail label={t('longitude')} value={coordinates?.lng} />
      <Detail label={t('new_season_fertilizer_type')} value={fertMeter?.type} />
      <Detail
        label={t('new_season_fertilizer_meter_id')}
        value={fertMeter?.id}
      />
      <Detail
        label={t('start_date')}
        value={formatUnixDate(fertMeter?.start_date)}
      />
    </div>
  );
};

const SensorsTable = ({ sensorRows, selectedKey, onSelect }) => {
  const { t } = useTranslation();
  if (sensorRows.length === 0) {
    return <div className='ns-empty-text'>{t('new_season_no_sensors')}</div>;
  }
  return (
    <table className='ns-table'>
      <thead>
        <tr>
          <th>{t('type')}</th>
          <th>{t('new_season_sensor_id')}</th>
          <th>{t('new_season_block')}</th>
        </tr>
      </thead>
      <tbody>
        {sensorRows.map((row) => (
          <tr
            key={row.key}
            className={row.key === selectedKey ? 'ns-table-row-selected' : ''}
            onClick={() => onSelect(row.key)}
          >
            <td>{row.sensor.type}</td>
            <td>{row.sensor.transmitter_id || row.serial}</td>
            <td>{row.blockName}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
};

const SensorsSection = ({ rows, details, mapBlocks, selectedBlockKeys }) => {
  const { t } = useTranslation();
  const sensorRows = useMemo(
    () => sensorRowsFor(rows, details),
    [rows, details],
  );
  const [selectedKey, setSelectedKey] = useState(null);
  const selected =
    sensorRows.find((row) => row.key === selectedKey) || sensorRows[0];
  const markers = useMemo(
    () =>
      sensorRows
        .map((row) => ({ row, coordinates: coordinatesOf(row.sensor) }))
        .filter((item) => item.coordinates)
        .map(({ row, coordinates }) => ({
          key: row.key,
          lat: coordinates.lat,
          lng: coordinates.lng,
          title: row.sensor.name || row.serial,
          selected: row.key === selected?.key,
          onClick: () => setSelectedKey(row.key),
        })),
    [sensorRows, selected?.key],
  );
  return (
    <div className='ns-section'>
      <div className='ns-section-title'>{t('sensors')}</div>
      <div className='ns-section-body'>
        <div className='ns-sensors-card'>
          <div className='ns-sensors-left'>
            <div className='ns-sensors-header'>
              <span>{t('new_season_sensors_table')}</span>
              <button
                type='button'
                className='ns-icon-btn'
                disabled
                title={t('new_season_sensors_read_only')}
              >
                +
              </button>
            </div>
            <DetailStatus rows={rows} details={details} />
            <SensorsTable
              sensorRows={sensorRows}
              selectedKey={selected?.key}
              onSelect={setSelectedKey}
            />
            <SensorDetails sensorRow={selected} />
            <div className='ns-muted mt-2'>
              {t('new_season_sensors_read_only')}
            </div>
          </div>
          <BlocksMap
            className='ns-map-small'
            blocks={mapBlocks}
            selectedKeys={selectedBlockKeys}
            focusKeys={selectedBlockKeys}
            markers={markers}
          />
        </div>
      </div>
    </div>
  );
};

module.exports = SensorsSection;
